/**
 * Profile field vocabularies (task 596).
 *
 * The Profile form's timezone and locale dropdowns must offer exactly what AMS
 * accepts — anything else is a 422 the User cannot act on. Both lists here are
 * built from the same sources AMS validates against
 * (`account-management-service/src/lib/validator/profileFields.ts`):
 *
 *   - Timezones: `Intl.supportedValuesOf('timeZone')`, the ECMA-402 list, plus
 *     an explicit `UTC`. That list contains only canonical non-Link zones, so
 *     it omits the whole `Etc/*` group and with it plain `UTC` — the single
 *     most likely value a User picks. AMS allows it explicitly; so does this.
 *     Because the browser and AMS read the same ECMA-402 data, this is a
 *     shared source rather than a copied list.
 *   - Locales: `SUPPORTED_LOCALES` is a deliberate mirror of the AMS constant.
 *     Innovate ships US English only — there is no i18n framework here and the
 *     Keycloak realm declares no additional locales — so one tag is the honest
 *     list. Adding a language to the product means adding its tag in AMS first.
 */

/** Display-name derivation sources, mirrored from the platform contract. */
export const SUPPORTED_LOCALES = ['en-US'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Human labels for the locale dropdown. */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  'en-US': 'English (United States)',
};

/**
 * `Intl.supportedValuesOf` is ES2022; the app compiles against an older lib,
 * so the call is typed here rather than through the global Intl types.
 */
type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

/** One region heading and the zones under it, for a grouped `<select>`. */
export interface TimezoneGroup {
  region: string;
  zones: string[];
}

let timezoneCache: string[] | null = null;

/**
 * Every IANA zone name the platform accepts, sorted. Built once — the list is
 * fixed for the lifetime of the browser's ICU data.
 */
export const supportedTimezones = (): string[] => {
  if (timezoneCache) return timezoneCache;

  const intl = Intl as IntlWithSupportedValues;
  const zones = typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : [];

  timezoneCache = Array.from(new Set([...zones, 'UTC'])).sort();
  return timezoneCache;
};

/**
 * The same zones grouped by their first path segment (`America/New_York` →
 * `America`), which is what makes a ~400-entry dropdown usable. Zones with no
 * region segment — `UTC` — are collected under "UTC" and sorted to the front,
 * since that is the one a User is most likely to want deliberately.
 */
export const timezoneGroups = (): TimezoneGroup[] => {
  const byRegion = new Map<string, string[]>();

  for (const zone of supportedTimezones()) {
    const region = zone.includes('/') ? zone.split('/')[0] : 'UTC';
    const zones = byRegion.get(region);
    if (zones) {
      zones.push(zone);
    } else {
      byRegion.set(region, [zone]);
    }
  }

  return Array.from(byRegion.entries())
    .map(([region, zones]) => ({ region, zones }))
    .sort((a, b) => {
      if (a.region === 'UTC') return -1;
      if (b.region === 'UTC') return 1;
      return a.region.localeCompare(b.region);
    });
};

/**
 * The zone the browser thinks the User is in, when it is one the platform
 * accepts. Used only to suggest a value on a profile that has none — never to
 * write one without the User choosing it.
 */
export const detectedTimezone = (): string | null => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return zone && supportedTimezones().includes(zone) ? zone : null;
};

/**
 * The initials AMS derives when a User has stored none — a deliberate mirror of
 * `deriveInitials` in `account-management-service/src/lib/userProfile.ts`:
 * accents folded, the first letter or digit of each word of `displayName`,
 * lowercased, at most five, `u` when nothing qualifies. "Kraig Vanderklomp" →
 * "kv".
 *
 * Used only as the Initials input's placeholder while the User has explicit
 * initials stored (task 1089), so they can see what clearing the field gives
 * them. When AMS reports `initialsSource: 'derived'`, the page shows the
 * server's own value instead.
 */
export const deriveInitials = (displayName: string | undefined | null): string => {
  const folded = (displayName ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const initials = folded
    .split(/\s+/)
    .map((word) => word.match(/[A-Za-z0-9]/)?.[0] ?? '')
    .join('')
    .toLowerCase()
    .slice(0, 5);

  return initials || 'u';
};
